/* The estate trend as geometry: everything the chart needs, computed without a browser.
 *
 * **A pure module, so the decisions a reader would most want to check are testable.** Where the
 * y-axis starts, whether a smoothed line can claim a value the data never held, and how a missing day
 * is spaced are all claims about a customer's data rather than styling, and none of them needs a DOM
 * to be wrong.
 *
 * **The curve is monotone cubic, and that is the one smoothing that is honest.** The reference draws
 * soft curves; a Catmull-Rom or plain Bézier fit through the same points overshoots — a series that
 * peaked at 88.4 draws a crest at 88.9 between two days, which is a score the estate never had,
 * printed on the screen whose whole subject is trusting the figures. Monotone interpolation
 * (Fritsch–Carlson, the shape d3's `curveMonotoneX` uses) never leaves the range of its two
 * endpoints, so every point on the line lies between two real measurements. A test holds that by
 * evaluating the curves rather than by trusting this paragraph.
 *
 * **x is time, not index.** `EstatePoint` documents that a day with nothing scored is *absent* from
 * the series, so index spacing would close every gap and draw a fortnight's silence as a single day.
 * Time spacing keeps the gap the width it was.
 *
 * **y is the observed range on clean steps, never 0–100.** A month between 82 and 89 plotted against
 * the full scale is a flat line — it would report "nothing changed" about a seven-point move. The
 * ticks are labelled, so the reader can see the axis does not start at zero; the existing panel argued
 * the same and showed the low and high beside it.
 */

import type { EstatePoint } from '@/shared/api/generated/scoring';

/** The drawing area: the full box and the margins the axes need. */
export interface PlotBox {
  readonly width: number;
  readonly height: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

export interface PlottedPoint {
  readonly x: number;
  readonly y: number;
  readonly value: number;
  readonly day: string;
  readonly scored: number;
}

export interface ValueTick {
  readonly y: number;
  readonly value: number;
}

export interface DayTick {
  readonly x: number;
  readonly day: string;
}

export interface TrendGeometry {
  readonly line: string;
  readonly area: string;
  readonly points: readonly PlottedPoint[];
  readonly valueTicks: readonly ValueTick[];
  readonly dayTicks: readonly DayTick[];
  /** The y-axis bottom: where the area's wash closes. */
  readonly baseline: number;
}

/** One cubic segment, exposed so a test can evaluate the curve instead of reading the path string. */
export interface Segment {
  readonly x0: number;
  readonly y0: number;
  readonly c1x: number;
  readonly c1y: number;
  readonly c2x: number;
  readonly c2y: number;
  readonly x1: number;
  readonly y1: number;
}

/** A score is a percentage; the axis may never claim a value the scale cannot hold. */
const SCORE_FLOOR = 0;
const SCORE_CEILING = 100;

/** Tick steps a reader can add in their head. 2.5 is deliberately absent: "62.5" on an axis is a
 * number somebody has to read twice, and a step of 2 or 5 always serves the same range. */
const STEPS = [0.5, 1, 2, 5, 10, 20, 25, 50] as const;

/** At most this many intervals, so the axis carries three to five labels — enough to read a value
 * off, few enough that the gridlines stay recessive. */
const MAX_INTERVALS = 4;

/** How many day labels the x-axis carries at most. */
const MAX_DAY_TICKS = 5;

export interface ScoreDomain {
  readonly floor: number;
  readonly ceiling: number;
  readonly step: number;
}

/** The y-axis extent: the observed range, widened to clean steps and clamped to 0–100.
 *
 * A flat series is padded by one point each way so the line sits in the middle of the plot rather
 * than on an axis edge, where it would read as "at the limit of the scale" — a claim it does not make.
 */
export function scoreDomain(values: readonly number[]): ScoreDomain {
  let low = Math.min(...values);
  let high = Math.max(...values);
  if (high - low < 1) {
    low -= 1;
    high += 1;
  }
  low = Math.max(SCORE_FLOOR, low);
  high = Math.min(SCORE_CEILING, high);

  for (const step of STEPS) {
    const floor = Math.max(SCORE_FLOOR, Math.floor(low / step) * step);
    const ceiling = Math.min(SCORE_CEILING, Math.ceil(high / step) * step);
    if (ceiling > floor && (ceiling - floor) / step <= MAX_INTERVALS) {
      return { floor, ceiling, step };
    }
  }
  return { floor: SCORE_FLOOR, ceiling: SCORE_CEILING, step: 25 };
}

/** Milliseconds since the epoch for a `YYYY-MM-DD` day, read as UTC so a reader's timezone cannot
 * move a point by a day. */
export function dayTime(day: string): number {
  const [year, month, date] = day.split('-').map(Number);
  return Date.UTC(year ?? 1970, (month ?? 1) - 1, date ?? 1);
}

/* d3's `curveMonotoneX` tangent rules. `slope3` is the interior tangent — zero at a local extremum,
   which is exactly what stops the curve overshooting there. */
function slope3(
  a: { x: number; y: number },
  b: { x: number; y: number },
  c: { x: number; y: number },
): number {
  const h0 = b.x - a.x;
  const h1 = c.x - b.x;
  /* Two readings on one day have no slope between them; a flat tangent is the only honest one. */
  if (h0 === 0 || h1 === 0) return 0;
  const s0 = (b.y - a.y) / h0;
  const s1 = (c.y - b.y) / h1;
  const p = (s0 * h1 + s1 * h0) / (h0 + h1);
  const tangent =
    (Math.sign(s0) + Math.sign(s1)) *
    Math.min(Math.abs(s0), Math.abs(s1), 0.5 * Math.abs(p));
  return Number.isFinite(tangent) ? tangent : 0;
}

/* The end tangents: a one-sided estimate from the neighbouring interior tangent. */
function slope2(
  a: { x: number; y: number },
  b: { x: number; y: number },
  t: number,
): number {
  const h = b.x - a.x;
  return h === 0 ? t : (3 * (b.y - a.y)) / h / 2 - t / 2;
}

/** The monotone cubic segments through a set of points sorted by x. */
export function monotoneSegments(
  points: readonly { readonly x: number; readonly y: number }[],
): Segment[] {
  const n = points.length;
  if (n < 2) return [];
  const tangents = new Array<number>(n).fill(0);
  if (n === 2) {
    const [a, b] = [points[0], points[1]];
    if (a !== undefined && b !== undefined) {
      const slope = b.x === a.x ? 0 : (b.y - a.y) / (b.x - a.x);
      tangents[0] = slope;
      tangents[1] = slope;
    }
  } else {
    for (let i = 1; i < n - 1; i += 1) {
      const a = points[i - 1];
      const b = points[i];
      const c = points[i + 1];
      if (a !== undefined && b !== undefined && c !== undefined)
        tangents[i] = slope3(a, b, c);
    }
    const p0 = points[0];
    const p1 = points[1];
    const pa = points[n - 2];
    const pb = points[n - 1];
    if (p0 !== undefined && p1 !== undefined)
      tangents[0] = slope2(p0, p1, tangents[1] ?? 0);
    if (pa !== undefined && pb !== undefined)
      tangents[n - 1] = slope2(pa, pb, tangents[n - 2] ?? 0);
  }

  const segments: Segment[] = [];
  for (let i = 0; i < n - 1; i += 1) {
    const a = points[i];
    const b = points[i + 1];
    if (a === undefined || b === undefined) continue;
    const dx = (b.x - a.x) / 3;
    const ta = tangents[i] ?? 0;
    const tb = tangents[i + 1] ?? 0;
    segments.push({
      x0: a.x,
      y0: a.y,
      c1x: a.x + dx,
      c1y: a.y + dx * ta,
      c2x: b.x - dx,
      c2y: b.y - dx * tb,
      x1: b.x,
      y1: b.y,
    });
  }
  return segments;
}

/* Two decimals: sub-pixel precision is invisible and the path string stays readable in a DOM dump. */
const fmt = (n: number): string => String(Math.round(n * 100) / 100);

/** The plotted trend, or `null` when there are fewer than two points.
 *
 * Two points are the minimum for a direction; one is a measurement, and drawing it as a "trend" would
 * be a chart claiming to show change it has no grounds for. The caller renders words instead.
 */
export function trendGeometry(
  series: readonly EstatePoint[],
  box: PlotBox,
): TrendGeometry | null {
  if (series.length < 2) return null;
  const sorted = [...series].sort((a, b) => dayTime(a.day) - dayTime(b.day));
  const domain = scoreDomain(sorted.map((point) => point.value));

  const plotWidth = box.width - box.left - box.right;
  const plotHeight = box.height - box.top - box.bottom;
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (first === undefined || last === undefined) return null;
  const t0 = dayTime(first.day);
  const span = dayTime(last.day) - t0;

  const xOf = (point: EstatePoint, index: number): number =>
    box.left +
    (span > 0
      ? ((dayTime(point.day) - t0) / span) * plotWidth
      : (index / (sorted.length - 1)) * plotWidth);
  const yOf = (value: number): number =>
    box.top + ((domain.ceiling - value) / (domain.ceiling - domain.floor)) * plotHeight;

  const points: PlottedPoint[] = sorted.map((point, index) => ({
    x: xOf(point, index),
    y: yOf(point.value),
    value: point.value,
    day: point.day,
    scored: point.scored,
  }));

  const segments = monotoneSegments(points);
  const start = points[0];
  const end = points[points.length - 1];
  if (start === undefined || end === undefined) return null;
  const line =
    `M${fmt(start.x)},${fmt(start.y)}` +
    segments
      .map(
        (s) =>
          `C${fmt(s.c1x)},${fmt(s.c1y)} ${fmt(s.c2x)},${fmt(s.c2y)} ${fmt(s.x1)},${fmt(s.y1)}`,
      )
      .join('');
  const baseline = box.top + plotHeight;
  const area = `${line}L${fmt(end.x)},${fmt(baseline)}L${fmt(start.x)},${fmt(baseline)}Z`;

  const valueTicks: ValueTick[] = [];
  for (let value = domain.floor; value <= domain.ceiling + 1e-9; value += domain.step) {
    const rounded = Math.round(value * 100) / 100;
    valueTicks.push({ y: yOf(rounded), value: rounded });
  }

  return { line, area, points, valueTicks, dayTicks: dayTicksOf(points), baseline };
}

/** Up to five day labels: always the first and the last, evenly between, never a duplicate. */
function dayTicksOf(points: readonly PlottedPoint[]): DayTick[] {
  const count = Math.min(MAX_DAY_TICKS, points.length);
  const indices = new Set<number>();
  for (let i = 0; i < count; i += 1) {
    indices.add(Math.round((i * (points.length - 1)) / Math.max(1, count - 1)));
  }
  return [...indices]
    .sort((a, b) => a - b)
    .flatMap((index) => {
      const point = points[index];
      return point === undefined ? [] : [{ x: point.x, day: point.day }];
    });
}

/** The plotted point nearest an x position — what the crosshair snaps to. Readers aim at a date. */
export function nearestPoint(
  points: readonly PlottedPoint[],
  x: number,
): PlottedPoint | undefined {
  let best: PlottedPoint | undefined;
  let distance = Number.POSITIVE_INFINITY;
  for (const point of points) {
    const d = Math.abs(point.x - x);
    if (d < distance) {
      best = point;
      distance = d;
    }
  }
  return best;
}

export interface TrendSnapshot {
  readonly latest: EstatePoint;
  readonly high: EstatePoint;
  readonly low: EstatePoint;
}

/** The latest reading and the window's extremes — the reference's "this week" panel, from the series
 * itself. A tie takes the most recent day, because "when was it last this good" is the useful reading. */
export function trendSnapshot(series: readonly EstatePoint[]): TrendSnapshot | null {
  if (series.length === 0) return null;
  const sorted = [...series].sort((a, b) => dayTime(a.day) - dayTime(b.day));
  const first = sorted[0];
  const latest = sorted[sorted.length - 1];
  if (first === undefined || latest === undefined) return null;
  let high = first;
  let low = first;
  for (const point of sorted) {
    if (point.value >= high.value) high = point;
    if (point.value <= low.value) low = point;
  }
  return { latest, high, low };
}
